import React, {
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    Document,
    Page,
    View,
    Text,
    Image,
    StyleSheet,
    PDFDownloadLink,
} from "@react-pdf/renderer";

import QRCode from "qrcode";

import api from "../../api/api";


/*
|--------------------------------------------------------------------------
| CR80 STANDARD ID CARD SIZE
|--------------------------------------------------------------------------
|
| Standard ID-1 / CR80:
|
| Width  = 85.60 mm
| Height = 53.98 mm
|
| PDF points:
|
| 1 mm = 2.83465 points
|
*/

const CARD_WIDTH =
    85.60 * 2.83465;

const CARD_HEIGHT =
    53.98 * 2.83465;


/*
|--------------------------------------------------------------------------
| PDF PAGE SETTINGS
|--------------------------------------------------------------------------
*/

const PAGE_PADDING =
    28;

const CARD_GAP =
    18;


/*
|--------------------------------------------------------------------------
| COLORS
|--------------------------------------------------------------------------
*/

const COLORS = {

    black:
        "#111827",

    orange:
        "#f97316",

    white:
        "#ffffff",

    gray:
        "#6b7280",

    lightGray:
        "#f3f4f6",

    border:
        "#d1d5db",

    green:
        "#166534",

    greenBackground:
        "#dcfce7",

};


/*
|--------------------------------------------------------------------------
| PDF STYLES
|--------------------------------------------------------------------------
*/

const styles =
    StyleSheet.create({

        /*
        |--------------------------------------------------------------------------
        | A4 PAGE
        |--------------------------------------------------------------------------
        */

        page: {

            width:
                "100%",

            minHeight:
                "100%",

            backgroundColor:
                COLORS.white,

            paddingTop:
                PAGE_PADDING,

            paddingBottom:
                PAGE_PADDING,

            paddingLeft:
                PAGE_PADDING,

            paddingRight:
                PAGE_PADDING,

            flexDirection:
                "row",

            flexWrap:
                "wrap",

            alignContent:
                "flex-start",

            justifyContent:
                "flex-start",

            columnGap:
                CARD_GAP,

            rowGap:
                CARD_GAP,

        },


        /*
        |--------------------------------------------------------------------------
        | INDIVIDUAL ID CARD
        |--------------------------------------------------------------------------
        */

        card: {

            width:
                CARD_WIDTH,

            height:
                CARD_HEIGHT,

            borderWidth:
                1,

            borderColor:
                COLORS.border,

            borderRadius:
                7,

            overflow:
                "hidden",

            backgroundColor:
                COLORS.white,

            position:
                "relative",

        },


        /*
        |--------------------------------------------------------------------------
        | CARD HEADER
        |--------------------------------------------------------------------------
        */

        cardHeader: {

            height:
                29,

            backgroundColor:
                COLORS.black,

            paddingLeft:
                9,

            paddingRight:
                9,

            flexDirection:
                "row",

            alignItems:
                "center",

            justifyContent:
                "space-between",

        },


        cardHeaderTitle: {

            color:
                COLORS.white,

            fontSize:
                7.5,

            fontWeight:
                "bold",

        },


        cardHeaderSubtitle: {

            color:
                "#d1d5db",

            fontSize:
                5,

        },


        /*
        |--------------------------------------------------------------------------
        | CARD BODY
        |--------------------------------------------------------------------------
        */

        cardBody: {

            flexDirection:
                "row",

            flex: 1,

            paddingTop:
                7,

            paddingBottom:
                6,

            paddingLeft:
                8,

            paddingRight:
                8,

        },


        /*
        |--------------------------------------------------------------------------
        | QR AREA
        |--------------------------------------------------------------------------
        */

        qrSection: {

            width:
                82,

            alignItems:
                "center",

            justifyContent:
                "center",

        },


        qrContainer: {

            width:
                72,

            height:
                72,

            backgroundColor:
                COLORS.white,

            alignItems:
                "center",

            justifyContent:
                "center",

        },


        qrImage: {

            width:
                70,

            height:
                70,

        },


        qrCaption: {

            marginTop:
                2,

            fontSize:
                4.3,

            color:
                COLORS.gray,

            textAlign:
                "center",

        },


        /*
        |--------------------------------------------------------------------------
        | TEACHER INFORMATION
        |--------------------------------------------------------------------------
        */

        teacherInfo: {

            flex:
                1,

            paddingLeft:
                7,

            justifyContent:
                "center",

        },


        teacherLabel: {

            fontSize:
                4.3,

            color:
                COLORS.gray,

            marginBottom:
                1,

            textTransform:
                "uppercase",

        },


        teacherName: {

            fontSize:
                10.5,

            fontWeight:
                "bold",

            color:
                COLORS.black,

            marginBottom:
                4,

        },


        detailRow: {

            marginBottom:
                2.5,

        },


        detailValue: {

            fontSize:
                6,

            color:
                COLORS.black,

            fontWeight:
                "bold",

        },


        /*
        |--------------------------------------------------------------------------
        | ACTIVE BADGE
        |--------------------------------------------------------------------------
        */

        activeBadge: {

            marginTop:
                2,

            backgroundColor:
                COLORS.greenBackground,

            paddingTop:
                2,

            paddingBottom:
                2,

            paddingLeft:
                5,

            paddingRight:
                5,

            borderRadius:
                3,

            alignSelf:
                "flex-start",

        },


        activeBadgeText: {

            fontSize:
                4.5,

            color:
                COLORS.green,

            fontWeight:
                "bold",

        },


        /*
        |--------------------------------------------------------------------------
        | FOOTER
        |--------------------------------------------------------------------------
        */

        cardFooter: {

            height:
                17,

            backgroundColor:
                COLORS.lightGray,

            paddingLeft:
                7,

            paddingRight:
                7,

            flexDirection:
                "row",

            alignItems:
                "center",

            justifyContent:
                "space-between",

        },


        footerText: {

            fontSize:
                4.2,

            color:
                COLORS.gray,

        },


        /*
        |--------------------------------------------------------------------------
        | EMPTY PDF
        |--------------------------------------------------------------------------
        */

        emptyPage: {

            flex:
                1,

            alignItems:
                "center",

            justifyContent:
                "center",

        },


        emptyTitle: {

            fontSize:
                16,

            fontWeight:
                "bold",

            color:
                COLORS.black,

            marginBottom:
                8,

        },


        emptyText: {

            fontSize:
                9,

            color:
                COLORS.gray,

            textAlign:
                "center",

        },

    });


/*
|--------------------------------------------------------------------------
| TEACHER NAME
|--------------------------------------------------------------------------
*/

const getTeacherName = (
    card
) => {

    return (

        card?.teacher?.full_name ||

        card?.teacher_name ||

        card?.teacher?.name ||

        card?.teacher?.username ||

        "Unknown Teacher"

    );

};


/*
|--------------------------------------------------------------------------
| TEACHER USERNAME
|--------------------------------------------------------------------------
*/

const getTeacherUsername = (
    card
) => {

    return (

        card?.teacher?.username ||

        card?.teacher_username ||

        card?.username ||

        "--"

    );

};


/*
|--------------------------------------------------------------------------
| SAFE FILE NAME
|--------------------------------------------------------------------------
*/

const sanitizeFileName = (
    value
) => {

    return String(
        value || "Teacher"
    )
        .trim()
        .replace(
            /[^a-zA-Z0-9]+/g,
            "_"
        )
        .replace(
            /^_+|_+$/g,
            ""
        );

};


/*
|--------------------------------------------------------------------------
| GENERATE QR CODE
|--------------------------------------------------------------------------
|
| We generate a large PNG before sending it to @react-pdf/renderer.
|
| Error correction level H gives the QR code strong recovery
| capability if the printed card gets slightly damaged or dirty.
|
*/

const generateQrCode = async (
    token
) => {

    if (!token) {

        return null;

    }


    try {

        const dataUrl =
            await QRCode.toDataURL(
                token,
                {

                    errorCorrectionLevel:
                        "H",

                    type:
                        "image/png",

                    margin:
                        2,

                    width:
                        600,

                    scale:
                        10,

                }
            );


        return dataUrl;

    } catch (error) {

        console.error(
            "Failed to generate teacher QR code:",
            error
        );


        return null;

    }

};


/*
|--------------------------------------------------------------------------
| INDIVIDUAL PDF CARD
|--------------------------------------------------------------------------
*/

const TeacherPDFCard = ({
    card,
    academicYear,
}) => {

    const [
        qrImage,
        setQrImage
    ] = useState(null);


    const [
        qrLoading,
        setQrLoading
    ] = useState(
        true
    );


    /*
    |--------------------------------------------------------------------------
    | GENERATE QR
    |--------------------------------------------------------------------------
    */

    useEffect(() => {

        let mounted =
            true;


        const createQr =
            async () => {

                if (
                    !card?.qr_token
                ) {

                    if (mounted) {

                        setQrImage(
                            null
                        );

                        setQrLoading(
                            false
                        );

                    }

                    return;

                }


                setQrLoading(
                    true
                );


                const image =
                    await generateQrCode(
                        card.qr_token
                    );


                if (mounted) {

                    setQrImage(
                        image
                    );

                    setQrLoading(
                        false
                    );

                }

            };


        createQr();


        return () => {

            mounted =
                false;

        };

    }, [
        card?.qr_token,
    ]);


    const teacherName =
        getTeacherName(
            card
        );


    const teacherUsername =
        getTeacherUsername(
            card
        );


    const cardNumber =
        card?.card_number ||
        "--";


    return (

        <View
            style={
                styles.card
            }
        >

            {/* ================================================= */}
            {/* HEADER */}
            {/* ================================================= */}

            <View
                style={
                    styles.cardHeader
                }
            >

                <Text
                    style={
                        styles.cardHeaderTitle
                    }
                >
                    TEACHER SMART ID - SICSS
                </Text>


                <Text
                    style={
                        styles.cardHeaderSubtitle
                    }
                >
                    QR ATTENDANCE
                </Text>

            </View>


            {/* ================================================= */}
            {/* BODY */}
            {/* ================================================= */}

            <View
                style={
                    styles.cardBody
                }
            >

                {/* ============================================= */}
                {/* QR */}
                {/* ============================================= */}

                <View
                    style={
                        styles.qrSection
                    }
                >

                    <View
                        style={
                            styles.qrContainer
                        }
                    >

                        {qrImage ? (

                            <Image
                                src={
                                    qrImage
                                }
                                style={
                                    styles.qrImage
                                }
                            />

                        ) : (

                            <Text
                                style={{
                                    fontSize:
                                        6,

                                    color:
                                        COLORS.gray,
                                }}
                            >
                                {qrLoading
                                    ? "QR"
                                    : "N/A"}
                            </Text>

                        )}

                    </View>


                    <Text
                        style={
                            styles.qrCaption
                        }
                    >
                        Scan for attendance
                    </Text>

                </View>


                {/* ============================================= */}
                {/* TEACHER DETAILS */}
                {/* ============================================= */}

                <View
                    style={
                        styles.teacherInfo
                    }
                >

                    <Text
                        style={
                            styles.teacherLabel
                        }
                    >
                        Teacher
                    </Text>


                    <Text
                        style={
                            styles.teacherName
                        }
                    >
                        {teacherName}
                    </Text>


                    <View
                        style={
                            styles.detailRow
                        }
                    >

                        <Text
                            style={
                                styles.teacherLabel
                            }
                        >
                            Card Number
                        </Text>


                        <Text
                            style={
                                styles.detailValue
                            }
                        >
                            {cardNumber}
                        </Text>

                    </View>


                    <View
                        style={
                            styles.detailRow
                        }
                    >

                        <Text
                            style={
                                styles.teacherLabel
                            }
                        >
                            Username
                        </Text>


                        <Text
                            style={
                                styles.detailValue
                            }
                        >
                            {teacherUsername}
                        </Text>

                    </View>


                    <View
                        style={
                            styles.detailRow
                        }
                    >

                        <Text
                            style={
                                styles.teacherLabel
                            }
                        >
                            Academic Year
                        </Text>


                        <Text
                            style={
                                styles.detailValue
                            }
                        >
                            {
                                academicYear ||
                                "--"
                            }
                        </Text>

                    </View>


                    <View
                        style={
                            styles.activeBadge
                        }
                    >

                        <Text
                            style={
                                styles.activeBadgeText
                            }
                        >
                            ACTIVE
                        </Text>

                    </View>

                </View>

            </View>


            {/* ================================================= */}
            {/* FOOTER */}
            {/* ================================================= */}

            <View
                style={
                    styles.cardFooter
                }
            >

                <Text
                    style={
                        styles.footerText
                    }
                >
                    Teacher Attendance - Design by: Polu School Management System
                </Text>


                <Text
                    style={
                        styles.footerText
                    }
                >
                    Secure QR Card
                </Text>

            </View>

        </View>

    );

};


/*
|--------------------------------------------------------------------------
| PDF DOCUMENT
|--------------------------------------------------------------------------
*/

const TeacherAttendancePDFDocument = ({
    cards,
    academicYear,
}) => {

    /*
    |--------------------------------------------------------------------------
    | SIX CARDS PER A4 PAGE
    |--------------------------------------------------------------------------
    |
    | 2 columns × 3 rows
    |
    */

    const pages =
        useMemo(
            () => {

                const result =
                    [];


                for (
                    let index = 0;
                    index < cards.length;
                    index += 6
                ) {

                    result.push(
                        cards.slice(
                            index,
                            index + 6
                        )
                    );

                }


                return result;

            },
            [
                cards,
            ]
        );


    /*
    |--------------------------------------------------------------------------
    | NO CARDS
    |--------------------------------------------------------------------------
    */

    if (
        !cards.length
    ) {

        return (

            <Document>

                <Page
                    size="A4"
                    style={
                        styles.emptyPage
                    }
                >

                    <Text
                        style={
                            styles.emptyTitle
                        }
                    >
                        No Active Teacher Cards
                    </Text>


                    <Text
                        style={
                            styles.emptyText
                        }
                    >
                        No active Teacher Attendance
                        Smart Cards were found for
                        {" "}
                        {
                            academicYear ||
                            "the selected academic year"
                        }.
                    </Text>

                </Page>

            </Document>

        );

    }


    /*
    |--------------------------------------------------------------------------
    | NORMAL PDF
    |--------------------------------------------------------------------------
    */

    return (

        <Document
            title={
                cards.length === 1

                    ? `Teacher Attendance Card - ${
                        getTeacherName(
                            cards[0]
                        )
                    }`

                    : "Teacher Attendance Smart Cards"
            }

            author={
                "School Management System"
            }

            subject={
                "Teacher QR Attendance Smart ID Cards"
            }

            creator={
                "School Management System"
            }
        >

            {pages.map(
                (
                    pageCards,
                    pageIndex
                ) => (

                    <Page
                        key={
                            `teacher-card-page-${pageIndex}`
                        }
                        size="A4"
                        orientation="portrait"
                        style={
                            styles.page
                        }
                    >

                        {pageCards.map(
                            (
                                currentCard
                            ) => (

                                <TeacherPDFCard
                                    key={
                                        currentCard.id ||
                                        currentCard.card_number
                                    }

                                    card={
                                        currentCard
                                    }

                                    academicYear={
                                        academicYear
                                    }

                                />

                            )
                        )}

                    </Page>

                )
            )}

        </Document>

    );

};


/*
|--------------------------------------------------------------------------
| MAIN COMPONENT
|--------------------------------------------------------------------------
|
| TWO MODES:
|
| 1. ALL CARD MODE
|
|    <TeacherAttendancePDF
|        academicYear={selectedYear}
|    />
|
|    Fetches all active teacher cards.
|
|
| 2. SINGLE CARD MODE
|
|    <TeacherAttendancePDF
|        academicYear={selectedYear}
|        card={card}
|    />
|
|    Generates ONLY that teacher's card.
|
|--------------------------------------------------------------------------
*/

const TeacherAttendancePDF = ({
    academicYear,

    card = null,

    buttonText =
        "Print All Teacher Cards",

    buttonStyle = {},

    icon = null,
}) => {

    const [
        cards,
        setCards
    ] = useState([]);


    const [
        loading,
        setLoading
    ] = useState(
        !card
    );


    const [
        error,
        setError
    ] = useState(
        null
    );


    /*
    |--------------------------------------------------------------------------
    | LOAD CARD DATA
    |--------------------------------------------------------------------------
    */

    useEffect(() => {

        let mounted =
            true;


        /*
        |--------------------------------------------------------------------------
        | SINGLE CARD MODE
        |--------------------------------------------------------------------------
        */

        if (card) {

            /*
            |--------------------------------------------------------------------------
            | Card must be active.
            |--------------------------------------------------------------------------
            */

            if (
                card.status !==
                "active"
            ) {

                if (mounted) {

                    setCards(
                        []
                    );

                    setError(
                        "Only active teacher cards can be printed."
                    );

                    setLoading(
                        false
                    );

                }


                return () => {

                    mounted =
                        false;

                };

            }


            /*
            |--------------------------------------------------------------------------
            | Card must have QR token.
            |--------------------------------------------------------------------------
            */

            if (
                !card.qr_token
            ) {

                if (mounted) {

                    setCards(
                        []
                    );

                    setError(
                        "This teacher card does not contain a QR token."
                    );

                    setLoading(
                        false
                    );

                }


                return () => {

                    mounted =
                        false;

                };

            }


            /*
            |--------------------------------------------------------------------------
            | USE ONLY THE PASSED CARD
            |--------------------------------------------------------------------------
            */

            if (mounted) {

                setCards([
                    card
                ]);

                setError(
                    null
                );

                setLoading(
                    false
                );

            }


            return () => {

                mounted =
                    false;

            };

        }


        /*
        |--------------------------------------------------------------------------
        | ALL CARDS MODE
        |--------------------------------------------------------------------------
        */

        const fetchAllCards =
            async () => {

                /*
                |--------------------------------------------------------------------------
                | Academic year is required.
                |--------------------------------------------------------------------------
                */

                if (
                    !academicYear?.id
                ) {

                    if (mounted) {

                        setCards(
                            []
                        );

                        setError(
                            "No academic year is selected."
                        );

                        setLoading(
                            false
                        );

                    }

                    return;

                }


                try {

                    setLoading(
                        true
                    );

                    setError(
                        null
                    );


                    const response =
                        await api.get(

                            "/api/smart-teacher-attendance/cards/",

                            {
                                params: {

                                    academic_year:
                                        academicYear.id,

                                    page_size:
                                        1000,

                                },

                            }

                        );


                    const data =
                        response.data ||
                        {};


                    let allCards =
                        [];


                    /*
                    |--------------------------------------------------------------------------
                    | DRF PAGINATION
                    |--------------------------------------------------------------------------
                    */

                    if (
                        Array.isArray(
                            data.results
                        )
                    ) {

                        allCards =
                            data.results;

                    }

                    /*
                    |--------------------------------------------------------------------------
                    | NON-PAGINATED RESPONSE
                    |--------------------------------------------------------------------------
                    */

                    else if (
                        Array.isArray(
                            data
                        )
                    ) {

                        allCards =
                            data;

                    }


                    /*
                    |--------------------------------------------------------------------------
                    | ONLY ACTIVE CARDS WITH QR TOKENS
                    |--------------------------------------------------------------------------
                    */

                    const activeCards =
                        allCards.filter(
                            currentCard => (

                                currentCard.status ===
                                "active"

                                &&

                                Boolean(
                                    currentCard.qr_token
                                )

                            )
                        );


                    if (mounted) {

                        setCards(
                            activeCards
                        );

                    }

                } catch (
                    requestError
                ) {

                    console.error(
                        "Failed to load teacher cards for PDF:",
                        requestError
                    );


                    if (mounted) {

                        setCards(
                            []
                        );


                        setError(

                            requestError
                                ?.response
                                ?.data
                                ?.detail ||

                            requestError
                                ?.response
                                ?.data
                                ?.message ||

                            "Unable to load teacher attendance cards."

                        );

                    }

                } finally {

                    if (mounted) {

                        setLoading(
                            false
                        );

                    }

                }

            };


        fetchAllCards();


        return () => {

            mounted =
                false;

        };

    }, [
        card,
        academicYear?.id,
    ]);


    /*
    |--------------------------------------------------------------------------
    | ERROR BUTTON
    |--------------------------------------------------------------------------
    */

    if (
        error
    ) {

        return (

            <button
                type="button"
                disabled
                title={
                    error
                }
                style={{

                    ...buttonStyle,

                    opacity:
                        0.6,

                    cursor:
                        "not-allowed",

                }}
            >

                {buttonText}

            </button>

        );

    }


    /*
    |--------------------------------------------------------------------------
    | LOADING BUTTON
    |--------------------------------------------------------------------------
    */

    if (
        loading
    ) {

        return (

            <button
                type="button"
                disabled
                style={{

                    ...buttonStyle,

                    opacity:
                        0.6,

                    cursor:
                        "wait",

                }}
            >

                Preparing Card...

            </button>

        );

    }


    /*
    |--------------------------------------------------------------------------
    | NO CARD AVAILABLE
    |--------------------------------------------------------------------------
    */

    if (
        !cards.length
    ) {

        return (

            <button
                type="button"
                disabled
                title={
                    card

                        ? "This teacher has no printable active card."

                        : "No active teacher cards found."
                }
                style={{

                    ...buttonStyle,

                    opacity:
                        0.6,

                    cursor:
                        "not-allowed",

                }}
            >

                {card
                    ? buttonText
                    : "No Teacher Cards"}

            </button>

        );

    }


    /*
    |--------------------------------------------------------------------------
    | FILE NAME
    |--------------------------------------------------------------------------
    */

    const fileName = card

        ? `Teacher_Attendance_Card_${
            sanitizeFileName(
                getTeacherName(
                    card
                )
            )
        }_${
            sanitizeFileName(
                card.card_number
            )
        }.pdf`

        : `Teacher_Attendance_Cards_${
            sanitizeFileName(
                academicYear?.name ||
                "Academic-Year"
            )
        }.pdf`;


    /*
    |--------------------------------------------------------------------------
    | BUTTON TEXT
    |--------------------------------------------------------------------------
    */

    const displayText = card

        ? buttonText

        : `${buttonText} (${cards.length})`;


    /*
    |--------------------------------------------------------------------------
    | PDF DOWNLOAD LINK
    |--------------------------------------------------------------------------
    */

    return (

        <PDFDownloadLink

            document={

                <TeacherAttendancePDFDocument

                    cards={
                        cards
                    }

                    academicYear={
                        academicYear?.name
                    }

                />

            }

            fileName={
                fileName
            }

            style={{

                textDecoration:
                    "none",

                display:
                    "inline-flex",

                alignItems:
                    "center",

                justifyContent:
                    "center",

                ...buttonStyle,

            }}

        >

            {({
                loading:
                    pdfLoading,
            }) => (

                <React.Fragment>

                    {icon}


                    {pdfLoading

                        ? "Generating PDF..."

                        : displayText

                    }

                </React.Fragment>

            )}

        </PDFDownloadLink>

    );

};


export default TeacherAttendancePDF;




